import { Box, Checkbox } from "@mui/material";
import {
  DateRange,
  MultiInputDateTimeRangeField,
} from "@mui/x-date-pickers-pro";
import { addDays, differenceInMinutes } from "date-fns";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  calculateDurationInMinutes,
  minutesToMs,
} from "../../../../app/common";
import { DAY_IN_MS } from "../../../../app/constants";
import usePrevious from "../../../../hooks/usePrevious";
import { EditEntry, LunchBreak } from "../../entriesAPI";

interface Props {
  entry: EditEntry;
  setEntry: (val: EditEntry) => void;
  disabled?: boolean;
  id: string;
  inputErrors: { [code: string]: boolean };
  setInputErrors: (inputErrors: { [code: string]: boolean }) => void;
}

const LunchBreakFields = ({
  entry,
  setEntry,
  disabled,
  id,
  inputErrors,
  setInputErrors,
}: Props) => {
  const { t } = useTranslation();
  const previousStartEnd = usePrevious({ start: entry.start, end: entry.end });

  const getLunchBreakDetails = useCallback(() => {
    // Starting delta is the value added to the starting time of the work shift.
    // It represents the number of minutes after the shift start.
    const startingDeltaMs =
      (entry?.defaultLunchBreak?.startDelta || 180) * 60000; // Use a default value of 180 minutes if startDelta is undefined

    // Add the delta to the starting time of the shift to find the starting point of the lunch break
    const lunchBreakStart = entry.start + startingDeltaMs;

    // The duration of the lunch break in minutes
    const durationMinutes = entry?.defaultLunchBreak?.length || 30;

    return {
      lunchBreakStart,
      durationMinutes,
    };
  }, [
    entry?.defaultLunchBreak?.length,
    entry?.defaultLunchBreak?.startDelta,
    entry.start,
  ]);

  useEffect(() => {
    const isEntryDurationChanged =
      (previousStartEnd?.start && entry.start !== previousStartEnd?.start) ||
      (previousStartEnd?.end && entry.end !== previousStartEnd?.end);

    const canSetLunchBreakAutomatically =
      entry.defaultLunchBreak?.setAutomaticallyAfter &&
      // entry start/end has changed or new entry
      (isEntryDurationChanged || !entry.id);

    if (canSetLunchBreakAutomatically) {
      const entryStart = new Date(entry.start);
      const entryEnd = new Date(entry.end);

      const entryDuration = differenceInMinutes(entryEnd, entryStart);

      if (
        entryDuration >=
        (entry?.defaultLunchBreak?.setAutomaticallyAfter as number)
      ) {
        const { lunchBreakStart, durationMinutes } = getLunchBreakDetails();

        // update the entry with the lunch break information with existing rules
        setEntry({
          ...entry,
          lunchBreak: {
            start: lunchBreakStart,
            duration: durationMinutes,
          },
        });
      } else {
        // remove the lunch break if the entry duration is less than the setAutomaticallyAfter value
        setEntry({
          ...entry,
          lunchBreak: null,
        });
      }
    }
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.start, entry.end, getLunchBreakDetails, setEntry]);

  const toggleLunchBreak = () => {
    if (entry && !entry.lunchBreak?.start) {
      // If the checkbox is checked, use default values from TES to add lunchBreak data to entry
      const { lunchBreakStart, durationMinutes } = getLunchBreakDetails();

      // Update the entry with the lunch break information
      entry &&
        setEntry({
          ...entry,
          lunchBreak: {
            start: lunchBreakStart,
            duration: durationMinutes,
          },
        });
    } else {
      // If the checkbox is unchecked, set the lunch break values to null in the entry
      entry &&
        setEntry({
          ...entry,
          lunchBreak: null,
        });
    }
  };

  const entryStart = new Date(entry.start);
  const entryEnd = new Date(entry.end);
  const entryStartDay = entryStart.getDate();
  const entryEndDay = addDays(entryStart, 1).getDate();

  const checkChangedTime = (newValue: DateRange<Date> | null) => {
    let startTimeChanged = false;
    let endTimeChanged = false;
    if (entry.lunchBreak?.start !== newValue?.[0]?.getTime()) {
      startTimeChanged = true;
    } else {
      endTimeChanged = true;
    }

    return { startTimeChanged, endTimeChanged };
  };

  const changeTime = (newValue: DateRange<Date> | null) => {
    // If invalid date range, return
    if (!newValue || !newValue[0] || !newValue[1]) return;
    const { startTimeChanged } = checkChangedTime(newValue);

    const startDate = newValue[0];
    const endDate = newValue[1];

    // We need to consider lunch break date only if entry exists for two different days
    if (entryStartDay !== entryEnd.getDate()) {
      // If end-time is smaller than start-time, set end-time for the next day
      if (startDate.getTime() > endDate.getTime()) {
        endDate.setDate(entryEndDay);
      }

      // If end-time is more than 24 hours from start-time, set end-time to the same day
      if (endDate.getTime() - startDate.getTime() > DAY_IN_MS) {
        endDate.setDate(entryStartDay);
      }

      // If start-time is after midnight, set it to the next day
      const midnight = new Date(startDate).setHours(0, 0, 0, 0);
      if (startDate.getTime() > midnight) {
        startDate.setDate(entryEndDay);
      }

      // If start-time is more than 24 hours from shift start, set start-time to the same day
      if (startDate.getTime() - entryStart.getTime() > DAY_IN_MS) {
        startDate.setDate(entryStartDay);
      }
    }

    const newStart = startDate.getTime();
    const newEnd = endDate.getTime();

    // Check if the resulting dates are valid
    if (!isNaN(newStart) && !isNaN(newEnd)) {
      // Update entry in state with new lunchbreak starting time, also recalculate duration

      // If lunch break start changes, set end time based on duration,
      // otherwise use the manually changed end time in calculation
      const newLunchBreak: LunchBreak = startTimeChanged
        ? {
            start: newStart,
            duration: calculateDurationInMinutes(
              newStart,
              newStart + minutesToMs(entry.lunchBreak?.duration || 0),
            ),
          }
        : {
            start: newStart,
            duration: calculateDurationInMinutes(newStart, newEnd),
          };

      entry &&
        setEntry({
          ...entry,
          lunchBreak: newLunchBreak,
        });

      // Check if lunch break is within the work shift
      if (newStart < entry.start || newEnd > entry.end) {
        // Lunch break is not within the work shift, set an error
        let errors = inputErrors;
        errors[id] = true;
        setInputErrors && setInputErrors({ ...errors });
      } else {
        // Remove the input error from the array if it exists
        let errors = inputErrors;
        if (errors[id]) {
          delete errors[id];
          setInputErrors && setInputErrors({ ...errors });
        }
      }
    } else {
      // Handle the case where the resulting dates are invalid
      let errors = inputErrors;
      errors[id] = true;
      setInputErrors && setInputErrors({ ...errors });
    }
  };

  return (
    <>
      <Box
        sx={{
          marginTop: "16px",
          fontSize: "14px",
        }}
      >
        <Checkbox
          checked={entry.lunchBreak?.start ? true : false}
          disabled={disabled}
          onChange={() => toggleLunchBreak()}
          sx={{
            padding: 0,
            marginRight: "8px",
          }}
        />
        {`${t("hourEntries:lunchBreak")} ${
          entry.lunchBreak?.duration ? `${entry.lunchBreak.duration}min` : ""
        }`}
      </Box>

      {entry?.lunchBreak?.start &&
        entry?.lunchBreak?.duration !== undefined &&
        entry?.lunchBreak?.duration !== null && (
          <MultiInputDateTimeRangeField
            format={"HH:mm"}
            value={[
              new Date(entry.lunchBreak.start),
              new Date(
                entry.lunchBreak.start + minutesToMs(entry.lunchBreak.duration),
              ),
            ]}
            onChange={(newValue) => {
              changeTime(newValue);
            }}
            disabled={disabled}
            paddingLeft={"0px"}
            slotProps={{
              textField: {
                size: "small",
                inputProps: {
                  style: {
                    padding: "5px 10px",
                  },
                },
              },
            }}
          />
        )}
    </>
  );
};

export default LunchBreakFields;
